import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Map, Marker, GoogleApiWrapper, InfoWindow } from 'google-maps-react';
import { LoadingContainer } from '../../_elements/LoadingContainer';
import { getMapCenterAndZoom } from '../../_helpers';
import MapPointer from '../../assets/icons/map_pointer.png';
import HealthCarePointer from '../../assets/icons/healtcare.jpg';
import RealEstatePointer from '../../assets/icons/realState.png';
// import ShopPointerPointer from '../../assets/icons/shop.png';
import HighlightPointer from '../../assets/icons/pointer.gif';
import {
    EventBadgeOverlay,
    EventRoutesOverlay,
    MapClusterer,
    MapInfoWindowContent,
    SearchChannelMarkers,
    TransitOverlay,
    WeatherOverlay,
} from './components';
import { resolvePin, PIN_SIZE } from './pinResolver';
import { useNativeChannelMarkers, useSearchFitBounds } from './utils';

const mapStyles = {
    position: 'absolute',
    width: '100%',
    height: 'auto',
};

const MARKER = {
    shop: HighlightPointer,
    default: MapPointer,
    'real state': RealEstatePointer,
    'health care': HealthCarePointer,
};
function GoogleMap({
    google,
    lat,
    lng,
    showPublicChannels = true,
    setChannelLatLong,
    isChannelCreateSelected,
    setShowCreateChannelModal,
    sholudUpdatePublicChannels,
    handleChatroomsEvents,
    currentMarker,
    searchedRoomId,
    setIsEvents,
    isEvents = false,
    chatroomEvents = null,
    publicChannels,
    handleCloseActiveEvent,
    handleHideEventsModal,
    upcomingEvents = [],
    showTraffic,
    showTransit,
    showEventRoutes,
    toggleEventRoutes,
    searchActive = false,
    showWeather = false,
    infoRenderer, // custom info renderer for selectedPlace (e.g., RE listings)
    onSelectPlace = () => {},
    ...rest
}) {
    const [center, setCenter] = useState({});
    useEffect(() => {
        setCenter({ lat: lat, lng: lng });
    }, [lat, lng]);
    const [activeMarker, setActiveMarker] = useState({});
    const [selectedPlace, setSelectedPlace] = useState({});
    const [showingInfoWindow, setShowingInfoWindow] = useState(false);
    const [chatroomId, setChatroomId] = useState('');
    const [tempChannels, setTempChannels] = useState([]);
    const [markersVersion, setMarkersVersion] = useState(0);

    const mapRef = useRef(null);
    const searchRefs = useRef([]);
    const nativeMarkersRef = useRef([]);
    const nativeMarkerMap = useRef({}); // channelId -> { marker, props }
    const markerDiscoveryRingsRef = useRef([]); // Google Maps Circles for discovery highlight

    useNativeChannelMarkers({
        google,
        mapRef,
        tempChannels,
        nativeMarkersRef,
        nativeMarkerMap,
        markerDiscoveryRingsRef,
        markerTypes: MARKER,
        pinSize: PIN_SIZE,
        resolvePin,
        setActiveMarker,
        setSelectedPlace,
        setChatroomId,
        setShowingInfoWindow,
        setCenter,
        setIsEvents,
        handleCloseActiveEvent,
        onSelectPlace,
    });

    // Bump markersVersion after native markers are created so MapClusterer
    // (which receives nativeMarkersRef.current at render time) re-runs its
    // effect with the updated markers array. Without this, the clusterer
    // captures the stale ref and never sees the new pins.
    useEffect(() => {
        if (nativeMarkersRef.current.length > 0) {
            setMarkersVersion((v) => v + 1);
        }
    }, [tempChannels]);

    useEffect(() => {
        // When searching (places/RE mode), bypass event filter — show all results.
        // Places results have Google Place IDs, not MongoDB _id, so event filtering
        // would eliminate them entirely and no pins would show.
        if (searchActive) {
            setTempChannels(publicChannels);
            return;
        }
        if (isEvents && chatroomEvents && chatroomEvents?.length > 0) {
            const taskChannelIds = new Set(
                chatroomEvents.flatMap((event) => {
                    const assignedChannels = Array.isArray(event?.assignedChannel)
                        ? event.assignedChannel
                        : event?.assignedChannel
                          ? [event.assignedChannel]
                          : [];
                    return assignedChannels
                        .map((channel) => (typeof channel === 'object' ? channel?._id || channel?.id : channel))
                        .filter(Boolean)
                        .map(String);
                })
            );
            const filteredArray = publicChannels?.filter((channel) =>
                taskChannelIds.has(String(channel?._id || channel?.id))
            );
            setTempChannels(filteredArray);
        } else {
            setTempChannels(publicChannels);
        }
    }, [chatroomEvents, isEvents, publicChannels, searchActive]);

    useEffect(() => {
        const getSelectedChatroomEvents = async () => {
            if (chatroomId) {
                if (/^[0-9a-fA-F]{24}$/.test(chatroomId)) {
                    handleChatroomsEvents(chatroomId);
                }
            }
        };
        getSelectedChatroomEvents();
    }, [chatroomId]);

    useEffect(() => {
        searchRefs.current = searchRefs.current.slice(0, publicChannels?.length);
    }, [publicChannels]);

    const onMarkerClick = (props, marker, e) => {
        window.setTimeout(() => {
            setActiveMarker(marker);
            setSelectedPlace(props);
            setChatroomId(props?.id);
            setShowingInfoWindow(true);
            setCenter(props?.position);
            setIsEvents(true);
            handleCloseActiveEvent();
            onSelectPlace(props);
        }, 100);
    };

    const onInfoWindowClose = () => {
        setActiveMarker({});
        setShowingInfoWindow(false);
        handleHideEventsModal();
        setChatroomId(null);
        setSelectedPlace({});
        if (isEvents && chatroomEvents && chatroomEvents?.length > 0) {
            handleChatroomsEvents(null, chatroomEvents?.[0]?.eventType?.id);
        } else {
            setIsEvents(false);
        }
    };

    const onMapClicked = async (mapProps, map, clickEvent) => {
        if (showingInfoWindow) {
            onInfoWindowClose(map);
        }
        if (isChannelCreateSelected) {
            let latitude = clickEvent.latLng.lat();
            let longitude = clickEvent.latLng.lng();
            setChannelLatLong({
                latitude: latitude,
                longitude: longitude,
            });
            setShowCreateChannelModal(true);
        }
    };

    useEffect(() => {
        if (!searchedRoomId) return;
        // Try native marker map first (works when not searching)
        const entry = nativeMarkerMap.current[searchedRoomId];
        if (entry) {
            const { marker, props } = entry;
            window.setTimeout(() => {
                setActiveMarker(marker);
                setSelectedPlace(props);
                setChatroomId(props?.id);
                setShowingInfoWindow(true);
                setCenter(props?.position);
                setIsEvents(true);
                handleCloseActiveEvent();
                onSelectPlace(props);
            }, 100);
            return;
        }
        // Fallback: React markers (when searching)
        if (searchRefs.current.length > 0) {
            const searchedMarker = searchRefs.current.find((markerRef) => markerRef?.props?.id === searchedRoomId);
            if (searchedMarker) {
                onMarkerClick(searchedMarker.props, searchedMarker.marker);
            }
        }
    }, [searchedRoomId, tempChannels]);

    useEffect(() => {
        if (isEvents && chatroomEvents?.length > 0 && tempChannels?.length > 0 && mapRef.current) {
            const locations = tempChannels
                .map((channelData) =>
                    channelData?.addresses
                        .map((channel) => ({ lat: Number(channel.latitude), lng: Number(channel.longitude) }))
                        .filter((location) => Number.isFinite(location.lat) && Number.isFinite(location.lng))
                )
                .flat();

            const { center: newCenter, zoom } = getMapCenterAndZoom(google, locations);
            setCenter(newCenter);
            mapRef.current.map.setZoom(zoom);
        }
    }, [chatroomEvents?.length, isEvents, google?.maps?.LatLng, google?.maps?.LatLngBounds, tempChannels]);

    // fitBounds whenever tempChannels has 1+ pins (search results, campus, places)
    // Runs when searchActive (places/RE mode) OR when not in event mode.
    // The isEvents guard is skipped for search results so the map centers on them.
    useSearchFitBounds({ google, mapRef, tempChannels, isEvents, searchActive, setCenter });

    useEffect(() => {
        if (!google || !mapRef || !mapRef?.current) return;
        let trafficLayer = null;
        if (showTraffic) {
            trafficLayer = new google.maps.TrafficLayer();
            trafficLayer.setMap(mapRef.current.map);
        }
        return () => {
            if (trafficLayer) {
                trafficLayer.setMap(null);
            }
        };
    }, [google, showTraffic, (mapRef || {}).current]);
    // When search yields 10+ results, cluster native markers instead of
    // rendering individual React pins (prevents overlapping pin soup).
    const searchClusterMode = searchActive && tempChannels?.length >= 10;

    return (
        <Fragment>
            {google && (
                <Map
                    google={google}
                    onClick={onMapClicked}
                    style={mapStyles}
                    ref={mapRef}
                    // onReady={() => setIsMapReady(true)}
                    zoom={15}
                    center={{ lat: center?.lat, lng: center?.lng }}
                    streetViewControlOptions={{
                        position: google.maps.ControlPosition.RIGHT_TOP,
                    }}
                    zoomControlOptions={{
                        position: google.maps.ControlPosition.RIGHT_TOP,
                    }}
                >
                    {showWeather && <WeatherOverlay lat={center?.lat} lng={center?.lng} show={true} />}
                    {showTransit && google && (mapRef?.current || {})?.map && (
                        <TransitOverlay
                            visible={true}
                            google={google}
                            mapRef={mapRef}
                            center={center}
                            map={mapRef.current.map}
                        />
                    )}
                    {showEventRoutes && (
                        <EventRoutesOverlay
                            visible={showEventRoutes}
                            google={google}
                            currentPosition={{ lat, lng }}
                            events={upcomingEvents}
                            onToggle={toggleEventRoutes}
                            mapRef={mapRef}
                            map={mapRef?.current?.map}
                        />
                    )}

                    {currentMarker ? (
                        <Marker
                            {...rest}
                            name={'current location'}
                            icon={{
                                url: currentMarker,
                                scaledSize: new google.maps.Size(PIN_SIZE.width, PIN_SIZE.height),
                            }}
                        />
                    ) : (
                        <Marker
                            {...rest}
                            name={'current location'}
                            position={{
                                lat: lat,
                                lng: lng,
                            }}
                        />
                    )}

                    <SearchChannelMarkers
                        google={google}
                        searchActive={searchActive}
                        searchClusterMode={searchClusterMode}
                        showPublicChannels={showPublicChannels}
                        tempChannels={tempChannels}
                        markerTypes={MARKER}
                        onMarkerClick={onMarkerClick}
                        searchRefs={searchRefs}
                    />
                    {/* Animated pin overlay for ALL search results — fade or pulse */}
                    {searchActive &&
                        !searchClusterMode &&
                        showPublicChannels &&
                        google &&
                        mapRef?.current?.map &&
                        tempChannels
                            ?.filter((ch) => ch?.addresses?.[0])
                            ?.map((channelData) => {
                                const pin = resolvePin(channelData, MARKER);
                                const eventIconUrl = channelData.matchedEventIcon || pin.eventIconUrl || null;
                                return channelData.addresses.map((channel, idx) => (
                                    <EventBadgeOverlay
                                        key={`badge-${channelData.id}-${idx}`}
                                        google={google}
                                        map={mapRef.current.map}
                                        position={{ lat: channel.latitude, lng: channel.longitude }}
                                        defaultPinUrl={pin.url}
                                        eventIconUrl={eventIconUrl}
                                        pinSize={PIN_SIZE}
                                        mode={eventIconUrl ? 'fade' : 'pulse'}
                                    />
                                ));
                            })}
                    {/* Zoom-aware clustering (active for non-search AND for 10+ search results) */}
                    {google && mapRef?.current?.map && (
                        <MapClusterer
                            google={google}
                            map={mapRef.current.map}
                            markers={nativeMarkersRef.current}
                            enableClustering={!searchActive || searchClusterMode}
                            pinSize={PIN_SIZE}
                            markersVersion={markersVersion}
                        />
                    )}
                    <InfoWindow
                        marker={activeMarker}
                        className="bg-dark"
                        onClose={onInfoWindowClose}
                        visible={showingInfoWindow && isEvents}
                    >
                        <MapInfoWindowContent infoRenderer={infoRenderer} selectedPlace={selectedPlace} />
                    </InfoWindow>
                </Map>
            )}
        </Fragment>
    );
}

export default GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    LoadingContainer: LoadingContainer,
})(GoogleMap);
